import { colors, COLORS } from '@constants/colors';
import { editorScrollbar } from '../styles';

export const useSceneTreeStyles = () => ({
  addButton: {
    width: '100%',
    color: colors.white_60,
    border: '1px dashed rgba(255, 255, 255, 0.16)',
    backgroundColor: 'transparent',
    textTransform: 'none',
    padding: '8px 10px',
    borderRadius: '8px',
    fontSize: '13px',
    fontWeight: 400,
    gap: '6px',
    justifyContent: 'flex-start',
    '&:hover': {
      borderColor: 'rgba(255, 255, 255, 0.3)',
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
      color: COLORS.WHITE,
    },
  },

  groupSection: {
    marginTop: '14px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },

  // The spawn-type picker, styled as the editor's dark glass popovers rather
  // than MUI's default light paper.
  addObjectMenuPaper: {
    background:
      'linear-gradient(135deg, rgba(28, 28, 34, 0.94) 0%, rgba(16, 16, 20, 0.94) 100%)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
    borderRadius: '12px',
    minWidth: '220px',
    color: '#e2e4ed',
    '& .MuiMenuItem-root': {
      fontSize: '13px',
      padding: '9px 14px',
      '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.08)' },
    },
  },

  // Unsaved-changes marker on a spawned-object row — mirrors the meaning of a
  // hotspot draft: what you see in-world is not what the backend stores yet.
  nodeDirtyDot: {
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: '#f0b357',
    flexShrink: 0,
    marginLeft: '6px',
  },

  // The prominent per-group create button (Add hotspot / Add object) — shared
  // so the two buttons cannot drift apart style-wise.
  groupAddButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    width: '100%',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '10px',
    padding: '9px 12px',
    margin: '2px 0 6px',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    color: 'inherit',
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
  },
  groupLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '2px 4px',
    marginBottom: '4px',
    borderRadius: '6px',
    transition: 'background-color 0.15s ease',
  },
  groupLabelRowCollapsible: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
    },
  },
  groupChevron: {
    color: colors.white_50,
    fontSize: 14,
    transition: 'transform 0.15s ease',
  },
  groupChevronCollapsed: {
    transform: 'rotate(-90deg)',
  },
  groupLabel: {
    color: colors.white_50,
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  /* Map editor catalog — a second level of grouping inside one tree section, so the
     category heading sits below groupLabel in weight rather than competing with it. */
  catalogCategory: {
    color: colors.white_40,
    fontSize: '10px',
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    padding: '6px 0 2px',
  },
  catalogGroup: {
    marginBottom: '6px',
  },
  // Catalogued but not yet built: still addable — UE simply skips it — so it is dimmed
  // rather than disabled, and the row keeps its hover and click affordances.
  catalogNodeUnbuilt: {
    opacity: 0.55,
  },
  catalogAddIcon: {
    fontSize: 15,
    color: colors.white_40,
  },
  groupCount: {
    color: colors.white_40,
    fontSize: '11px',
    fontWeight: 500,
  },
  comingSoonBadge: {
    color: colors.white_50,
    fontSize: '10px',
    fontWeight: 500,
    textTransform: 'lowercase',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '6px',
    padding: '1px 6px',
    marginLeft: '6px',
  },

  node: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 8px',
    borderRadius: '8px',
    cursor: 'pointer',
    color: colors.white_60,
    minHeight: '34px',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      color: COLORS.WHITE,
    },
  },
  nodeSelected: {
    backgroundColor: 'rgba(69, 84, 229, 0.18)',
    color: COLORS.WHITE,
    '&:hover': {
      backgroundColor: 'rgba(69, 84, 229, 0.22)',
    },
  },
  nodeDisabled: {
    cursor: 'not-allowed',
    opacity: 0.45,
    '&:hover': {
      backgroundColor: 'transparent',
      color: colors.white_60,
    },
  },
  nodeIcon: {
    fontSize: 18,
    width: 18,
    height: 18,
    color: 'inherit',
    flexShrink: 0,
  },
  nodeThumb: {
    width: 18,
    height: 18,
    borderRadius: '4px',
    objectFit: 'cover',
    flexShrink: 0,
    border: `1px solid ${colors.white_20}`,
  },
  nodeLabelRow: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minWidth: 0,
  },
  nodeContentThumb: {
    width: 16,
    height: 16,
    borderRadius: '3px',
    objectFit: 'cover',
    flexShrink: 0,
    border: `1px solid ${colors.white_20}`,
  },
  nodeLabel: {
    flex: 1,
    fontSize: '13px',
    fontWeight: 500,
    color: 'inherit',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  nodeEye: {
    color: colors.white_40,
    padding: '2px',
    '&:hover': {
      color: COLORS.WHITE,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  nodeEyeHidden: {
    color: colors.white_30,
  },

  nodeActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '2px',
    flexShrink: 0,
  },

  nodeAction: {
    color: colors.white_40,
    padding: '2px',
    transition: 'color 0.15s ease',
    '&:hover': {
      color: COLORS.WHITE,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },

  emptyText: {
    color: colors.white_40,
    fontSize: '12px',
    padding: '6px 8px',
    fontStyle: 'italic',
  },

  // Vertical focus rail (Map / Spots / Scripts) — VS Code / Webflow style.
  railLayout: {
    display: 'flex',
    gap: '10px',
    flex: 1,
    minHeight: 0,
    padding: '0 12px 12px',
  },
  // Applied while a spot's media is mid-upload, so the tree can't be used to
  // navigate away and abandon the in-flight upload.
  railLayoutLocked: {
    opacity: 0.45,
    pointerEvents: 'none',
    cursor: 'not-allowed',
  },
  rail: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '7px 5px',
    backgroundColor: 'rgba(255, 255, 255, 0.035)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    // Stretch to the full panel height so the focus rail reads as a distinct
    // column separating the categories from the working area.
    alignSelf: 'stretch',
    flex: 'none',
  },
  railItem: {
    width: 46,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    padding: '8px 2px 7px',
    borderRadius: '9px',
    color: colors.white_50,
    cursor: 'pointer',
    transition: 'all 0.13s ease',
    '& svg': {
      fontSize: 16,
    },
    '&:hover': {
      color: COLORS.WHITE,
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
    },
  },
  railItemActive: {
    backgroundColor: 'rgba(124, 111, 240, 0.18)',
    color: '#CFC8FF',
    '&:hover': {
      backgroundColor: 'rgba(124, 111, 240, 0.22)',
    },
  },
  railItemLabel: {
    fontSize: '8.5px',
    fontWeight: 600,
    letterSpacing: '0.02em',
    lineHeight: 1,
    // The rail is a fixed 46px column, so a label wider than that used to spill
    // straight out of the active pill. Clipped instead — the tooltip carries the
    // full name, and no future label can break the strip.
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  railBody: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflowY: 'auto',
    paddingRight: '2px',
    ...editorScrollbar,
  },

  driftGroupLabelAmber: {
    color: '#f0b357',
  },
  coverageMarker: {
    fontSize: 15,
    flexShrink: 0,
  },
  coverageConfigured: {
    color: '#5fd39a',
  },
  coveragePlaceholder: {
    color: colors.white_30,
  },
  ghostMarkerMissing: {
    fontSize: 16,
    flexShrink: 0,
    color: '#f0b357',
  },
  ghostNodeLabel: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  ghostNodeTitleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    minWidth: 0,
  },
  ghostNodeTitle: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'inherit',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  ghostNodeSub: {
    fontSize: '10px',
    color: colors.white_40,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  newBadge: {
    fontSize: '9px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    padding: '1px 5px',
    borderRadius: '5px',
    backgroundColor: 'rgba(124, 108, 240, 0.22)',
    color: '#c8bdff',
    flexShrink: 0,
    textTransform: 'uppercase',
  },
  driftNote: {
    color: colors.white_40,
    fontSize: '11.5px',
    lineHeight: 1.5,
    padding: '8px 8px 2px',
  },

  nodeBadge: {
    fontSize: '10px',
    fontWeight: 600,
    padding: '1px 6px',
    borderRadius: '999px',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  nodeBadgeFull: {
    backgroundColor: 'rgba(74, 222, 128, 0.16)',
    color: '#86efac',
  },
  nodeBadgePartial: {
    backgroundColor: 'rgba(250, 204, 21, 0.16)',
    color: '#fde68a',
  },
  nodeBadgeEmpty: {
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    color: colors.white_40,
  },
});
